export interface DimensionColorInput {
  key: string;
  label: string;
}

export interface DimensionColorCollection {
  scope: string;
  dimensions: readonly DimensionColorInput[];
}

interface Oklab {
  l: number;
  a: number;
  b: number;
}

interface ColorCandidate extends Oklab {
  hueOffset: number;
  toneOffset: number;
  sourceRank: number;
}

interface SourceColor {
  hue: number;
  l: number;
  chroma: number;
}

export interface DimensionColorInfo {
  color: string;
  emoji: string | null;
  sourceHue: number;
  sourceHues: readonly number[];
}

const MIN_COLOR_DISTANCE = 0.09;
const assignments = new Map<string, DimensionColorInfo>();
const labelAssignments = new Map<string, DimensionColorInfo>();
const sourceColorCache = new Map<string, readonly SourceColor[]>();

function stableHash(value: string): number {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function extractLeadingEmoji(label: string): string | null {
  const value = label.trimStart();
  if (!value) return null;
  const segment = Array.from(
    new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value),
  )[0]?.segment;
  if (!segment) return null;
  return /\p{Extended_Pictographic}|\p{Regional_Indicator}|[0-9#*]\uFE0F?\u20E3/u.test(segment)
    ? segment
    : null;
}

function srgbChannel(value: number): number {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function rgbToOklab(red: number, green: number, blue: number): Oklab {
  const r = srgbChannel(red);
  const g = srgbChannel(green);
  const b = srgbChannel(blue);
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const lRoot = Math.cbrt(l);
  const mRoot = Math.cbrt(m);
  const sRoot = Math.cbrt(s);
  return {
    l: 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot,
    a: 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot,
    b: 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot,
  };
}

function oklabToHex({ l, a, b }: Oklab): string {
  const lRoot = l + 0.3963377774 * a + 0.2158037573 * b;
  const mRoot = l - 0.1055613458 * a - 0.0638541728 * b;
  const sRoot = l - 0.0894841775 * a - 1.291485548 * b;
  const linear = [
    4.0767416621 * lRoot ** 3 - 3.3077115913 * mRoot ** 3 + 0.2309699292 * sRoot ** 3,
    -1.2684380046 * lRoot ** 3 + 2.6097574011 * mRoot ** 3 - 0.3413193965 * sRoot ** 3,
    -0.0041960863 * lRoot ** 3 - 0.7034186147 * mRoot ** 3 + 1.707614701 * sRoot ** 3,
  ];
  const encoded = linear.map((channel) => {
    const value = channel <= 0.0031308 ? 12.92 * channel : 1.055 * channel ** (1 / 2.4) - 0.055;
    return Math.round(Math.max(0, Math.min(1, value)) * 255);
  });
  return `#${encoded.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function hexToOklab(hex: string): Oklab {
  return rgbToOklab(
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  );
}

function degrees(radians: number): number {
  return ((radians * 180) / Math.PI + 360) % 360;
}

function radians(degreesValue: number): number {
  return (degreesValue * Math.PI) / 180;
}

function colorsFromEmoji(emoji: string): SourceColor[] {
  if (typeof document === 'undefined') return [];
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 80;
    canvas.height = 80;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return [];
    context.clearRect(0, 0, 80, 80);
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = '58px "Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
    context.fillText(emoji, 40, 42);
    const pixels = context.getImageData(0, 0, 80, 80).data;
    const bins = Array.from({ length: 24 }, () => ({
      weight: 0,
      x: 0,
      y: 0,
      lightness: 0,
      chroma: 0,
    }));
    for (let index = 0; index < pixels.length; index += 4) {
      const alpha = (pixels[index + 3] ?? 0) / 255;
      if (alpha < 0.18) continue;
      const lab = rgbToOklab(pixels[index] ?? 0, pixels[index + 1] ?? 0, pixels[index + 2] ?? 0);
      const chroma = Math.hypot(lab.a, lab.b);
      if (chroma < 0.025 || lab.l < 0.16 || lab.l > 0.94) continue;
      const hue = degrees(Math.atan2(lab.b, lab.a));
      const weight = alpha * chroma ** 1.35 * (0.55 + Math.min(lab.l, 0.8));
      const bin = bins[Math.floor(hue / 15) % bins.length]!;
      bin.weight += weight;
      bin.x += Math.cos(radians(hue)) * weight;
      bin.y += Math.sin(radians(hue)) * weight;
      bin.lightness += lab.l * weight;
      bin.chroma += chroma * weight;
    }
    const ranked = bins
      .map((bin, index) => ({ index, weight: bin.weight }))
      .sort((left, right) => right.weight - left.weight);
    const dominantWeight = ranked[0]?.weight ?? 0;
    if (dominantWeight === 0) return [];
    const colors: SourceColor[] = [];
    for (const { index, weight } of ranked) {
      if (colors.length === 3 || weight < dominantWeight * 0.12) break;
      let x = 0;
      let y = 0;
      let lightness = 0;
      let chroma = 0;
      let mergedWeight = 0;
      for (const offset of [-1, 0, 1]) {
        const bin = bins[(index + offset + bins.length) % bins.length]!;
        x += bin.x;
        y += bin.y;
        lightness += bin.lightness;
        chroma += bin.chroma;
        mergedWeight += bin.weight;
      }
      const hue = degrees(Math.atan2(y, x));
      const farFromExisting = colors.every((existing) => {
        const difference = Math.abs(existing.hue - hue);
        return Math.min(difference, 360 - difference) >= 32;
      });
      if (farFromExisting && mergedWeight > 0)
        colors.push({
          hue,
          l: lightness / mergedWeight,
          chroma: chroma / mergedWeight,
        });
    }
    return colors;
  } catch {
    return [];
  }
}

function sourceColors(label: string, emoji: string | null): readonly SourceColor[] {
  const cacheKey = emoji ?? `label:${label}`;
  const cached = sourceColorCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const detected = emoji ? colorsFromEmoji(emoji) : [];
  const colors = detected.length
    ? detected
    : [{ hue: stableHash(cacheKey) % 360, l: 0.635, chroma: 0.155 }];
  sourceColorCache.set(cacheKey, colors);
  return colors;
}

function colorDistance(left: Oklab, right: Oklab): number {
  return Math.hypot(left.l - right.l, left.a - right.a, left.b - right.b);
}

function candidateAt(
  source: SourceColor,
  sourceRank: number,
  hueOffset: number,
  toneOffset: number,
  chromaFactor: number,
): ColorCandidate {
  const adjustedHue = (source.hue + hueOffset + 360) % 360;
  const baseLightness = Math.max(0.52, Math.min(0.79, source.l));
  const l = Math.max(0.4, Math.min(0.82, baseLightness + toneOffset));
  const minimumChroma = adjustedHue >= 68 && adjustedHue <= 112 ? 0.15 : 0.09;
  const chroma = Math.max(minimumChroma, Math.min(0.215, source.chroma * chromaFactor));
  return {
    l,
    a: Math.cos(radians(adjustedHue)) * chroma,
    b: Math.sin(radians(adjustedHue)) * chroma,
    hueOffset,
    toneOffset,
    sourceRank,
  };
}

function candidatesFor(
  sources: readonly SourceColor[],
  hash: number,
  preserveEmojiHue: boolean,
): ColorCandidate[] {
  const direction = (hash & 1) === 0 ? 1 : -1;
  const hueOffsets = preserveEmojiHue
    ? [0]
    : [
        0,
        24 * direction,
        -24 * direction,
        48 * direction,
        -48 * direction,
        84 * direction,
        -84 * direction,
        132 * direction,
        -132 * direction,
        180,
      ];
  const tones = [0, -0.1, 0.08, -0.05, 0.04, -0.16, 0.14, -0.24, 0.2, -0.31];
  const chromaFactors = [1, 0.72, 1.2, 0.5];
  const candidates: ColorCandidate[] = [];
  for (const hueOffset of hueOffsets) {
    for (const [sourceRank, source] of sources.entries()) {
      for (const toneOffset of tones) {
        for (const chromaFactor of chromaFactors)
          candidates.push(candidateAt(source, sourceRank, hueOffset, toneOffset, chromaFactor));
      }
    }
  }
  return candidates;
}

function assignCollection(collection: DimensionColorCollection): void {
  const assigned: Oklab[] = [];
  const uniqueDimensions = [...collection.dimensions].filter(
    (dimension, index, all) => all.findIndex((item) => item.key === dimension.key) === index,
  );
  const emojiCounts = new Map<string, number>();
  for (const dimension of uniqueDimensions) {
    const emoji = extractLeadingEmoji(dimension.label);
    if (emoji) emojiCounts.set(emoji, (emojiCounts.get(emoji) ?? 0) + 1);
  }
  const dimensions = uniqueDimensions
    .map((dimension) => {
      const emoji = extractLeadingEmoji(dimension.label);
      return { dimension, emoji, sources: sourceColors(dimension.label, emoji) };
    })
    .sort((left, right) => {
      if ((left.emoji === null) !== (right.emoji === null)) return left.emoji === null ? 1 : -1;
      if (left.sources.length !== right.sources.length)
        return left.sources.length - right.sources.length;
      const lightnessDifference = (right.sources[0]?.l ?? 0) - (left.sources[0]?.l ?? 0);
      if (Math.abs(lightnessDifference) > 0.001) return lightnessDifference;
      return (
        stableHash(`${collection.scope}:${left.dimension.key}`) -
        stableHash(`${collection.scope}:${right.dimension.key}`)
      );
    });

  for (const descriptor of dimensions) {
    const { dimension, emoji, sources } = descriptor;
    const shared = labelAssignments.get(dimension.label);
    if (shared) {
      assignments.set(dimension.key, shared);
      assigned.push(hexToOklab(shared.color));
      continue;
    }
    const candidateSources =
      emoji && (emojiCounts.get(emoji) ?? 0) > 1 ? sources.slice(0, 1) : sources;
    const candidates = candidatesFor(candidateSources, stableHash(dimension.key), emoji !== null);
    let selected = candidates[0]!;
    let selectedLab = hexToOklab(oklabToHex(selected));
    let bestDistance = -1;
    for (const candidate of candidates) {
      const rendered = hexToOklab(oklabToHex(candidate));
      const distance = assigned.length
        ? Math.min(...assigned.map((current) => colorDistance(rendered, current)))
        : Number.POSITIVE_INFINITY;
      if (distance >= MIN_COLOR_DISTANCE) {
        selected = candidate;
        selectedLab = rendered;
        break;
      }
      const fidelityPenalty =
        candidate.sourceRank * 0.006 +
        Math.abs(candidate.hueOffset) / 1000 +
        Math.abs(candidate.toneOffset) / 4;
      const score = distance - fidelityPenalty;
      if (score > bestDistance) {
        bestDistance = score;
        selected = candidate;
        selectedLab = rendered;
      }
    }
    assigned.push(selectedLab);
    const sourceHues = sources.map((source) => source.hue);
    const info = {
      color: oklabToHex(selected),
      emoji,
      sourceHue: sourceHues[0]!,
      sourceHues,
    };
    assignments.set(dimension.key, info);
    labelAssignments.set(dimension.label, info);
  }
}

export function configureDimensionColors(collections: readonly DimensionColorCollection[]): void {
  assignments.clear();
  labelAssignments.clear();
  for (const collection of collections) assignCollection(collection);
}

export function dimensionColorInfo(key: string, label: string): DimensionColorInfo {
  const existing = assignments.get(key) ?? labelAssignments.get(label);
  if (existing) return existing;
  assignCollection({ scope: 'runtime', dimensions: [{ key, label }] });
  return assignments.get(key)!;
}

export function dimensionColor(key: string, label: string): string {
  return dimensionColorInfo(key, label).color;
}
